import { ApiClientError } from '@/lib/api/client'
import { landingApiRequest } from '@/lib/landing-api'
import {
  parseAvatarConfig,
  type AvatarConfig,
} from '@/features/donorAvatar/avatar-catalog'
import {
  parseMascotConfig,
  type MascotConfig,
} from '@/features/donorMascot/mascot-catalog'
import type { LandingAchievementId } from '@/lib/landing-achievements'

export type LandingTopDonor = {
  rank: number
  display_name: string
  profile_photo_url: string | null
  avatar?: AvatarConfig | null
  mascot?: MascotConfig | null
  mascot_level?: number | null
  total_amount: string
  paid_purchase_count?: number | null
  achievement_ids?: LandingAchievementId[] | null
}

export type LandingCampaignTopDonorsPayload = {
  campaign_id: number
  viewer_total_amount?: string | null
  viewer_rank?: number | null
  items: LandingTopDonor[]
}

const MAX_RANKING_ITEMS = 10
const PUBLIC_ACHIEVEMENT_IDS: LandingAchievementId[] = [
  'first_purchase',
  'frequent_supporter',
  'special_supporter',
  'profile_complete',
  'avatar_customized',
  'scratch_card',
]

export async function landingFetchCampaignTopDonors(
  campaignId: number
): Promise<LandingCampaignTopDonorsPayload> {
  if (!Number.isInteger(campaignId) || campaignId <= 0) {
    throw new ApiClientError(
      'Não foi possível carregar o ranking. Tente de novo em instantes.',
      400
    )
  }

  const payload = await landingApiRequest<LandingCampaignTopDonorsPayload>(
    `/api/me/campaigns/${campaignId}/top-donors`,
    { method: 'GET' }
  )

  return normalizeTopDonorsPayload(payload)
}

export function normalizeTopDonorsPayload(
  payload: LandingCampaignTopDonorsPayload
): LandingCampaignTopDonorsPayload {
  const campaignId = Number(payload.campaign_id)
  const rawItems = Array.isArray(payload.items) ? payload.items : []
  const viewerTotal = payload.viewer_total_amount
  const viewerRank = Number(payload.viewer_rank)

  return {
    campaign_id: Number.isInteger(campaignId) && campaignId > 0 ? campaignId : 0,
    viewer_total_amount:
      typeof viewerTotal === 'string' && /^\d+(?:\.\d{1,2})?$/.test(viewerTotal)
        ? viewerTotal
        : null,
    viewer_rank:
      payload.viewer_rank != null &&
      Number.isInteger(viewerRank) &&
      viewerRank >= 1 &&
      viewerRank <= MAX_RANKING_ITEMS
        ? viewerRank
        : null,
    items: rawItems.slice(0, MAX_RANKING_ITEMS).flatMap((item) => {
      const normalized = normalizeTopDonor(item)
      return normalized ? [normalized] : []
    }),
  }
}

export function isSafeTopDonorPhotoUrl(url: string): boolean {
  const trimmed = url.trim()
  if (trimmed.startsWith('/')) {
    return (
      !trimmed.startsWith('//') &&
      !trimmed.includes('..') &&
      /^\/[A-Za-z0-9._/-]+$/.test(trimmed)
    )
  }

  try {
    const parsed = new URL(trimmed)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

export function formatTopDonorAmount(amount: string | number): string {
  const parsed = typeof amount === 'number' ? amount : Number(amount)
  if (!Number.isFinite(parsed)) {
    return 'R$ 0,00'
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(parsed)
}

export function topDonorInitial(displayName: string): string {
  const trimmed = displayName.trim()
  if (trimmed === '') {
    return 'D'
  }

  return trimmed.charAt(0).toUpperCase()
}

function normalizeTopDonor(item: LandingTopDonor): LandingTopDonor | null {
  const rank = Number(item.rank)
  const displayName =
    typeof item.display_name === 'string' ? item.display_name.trim() : ''
  const totalAmount =
    typeof item.total_amount === 'string' || typeof item.total_amount === 'number'
      ? String(item.total_amount)
      : ''

  if (
    !Number.isInteger(rank) ||
    rank < 1 ||
    rank > MAX_RANKING_ITEMS ||
    displayName === ''
  ) {
    return null
  }

  const photoUrl =
    typeof item.profile_photo_url === 'string' &&
    isSafeTopDonorPhotoUrl(item.profile_photo_url)
      ? item.profile_photo_url.trim()
      : null
  const mascot = parseMascotConfig(item.mascot)

  return {
    rank,
    display_name: maskTopDonorName(displayName),
    profile_photo_url: photoUrl,
    avatar: parseAvatarConfig(item.avatar),
    mascot,
    mascot_level:
      mascot &&
      Number.isInteger(item.mascot_level) &&
      Number(item.mascot_level) >= 1 &&
      Number(item.mascot_level) <= 50
        ? Number(item.mascot_level)
        : null,
    total_amount: totalAmount,
    paid_purchase_count:
      Number.isSafeInteger(item.paid_purchase_count) &&
      Number(item.paid_purchase_count) >= 0
        ? Number(item.paid_purchase_count)
        : null,
    achievement_ids: Array.isArray(item.achievement_ids)
      ? PUBLIC_ACHIEVEMENT_IDS.filter((id) => item.achievement_ids?.includes(id))
      : null,
  }
}

function maskTopDonorName(name: string): string {
  const [first, ...rest] = name.split(/\s+/u)
  return [first, ...rest.map((token) => `${Array.from(token)[0]}***`)].join(' ')
}
